// Phase 7 R1 — list & filter purchase orders for a shop.
// Phase 7 R5 — extended with date-range and free-text search filters.
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

export type PurchaseOrderStatus =
  | 'draft' | 'submitted' | 'partially_received' | 'received'
  | 'partially_returned' | 'closed' | 'canceled';

export interface PurchaseOrder {
  id: string;
  shop_id: string;
  po_number: string;
  vendor_name: string | null;
  status: PurchaseOrderStatus;
  ordered_at: string | null;
  expected_at: string | null;
  received_at: string | null;
  notes: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface UsePurchaseOrdersFilters {
  status?: PurchaseOrderStatus | 'all';
  vendor?: string;
  /** R5: ISO date (yyyy-mm-dd) — created_at >= dateFrom (start of day) */
  dateFrom?: string;
  /** R5: ISO date (yyyy-mm-dd) — created_at <= dateTo (end of day) */
  dateTo?: string;
  /** R5: free-text — matches po_number OR vendor_name (case-insensitive) */
  search?: string;
}

export function usePurchaseOrders(filters: UsePurchaseOrdersFilters = {}) {
  const { activeShop } = useAuth();
  const shopId = activeShop?.id;

  return useQuery({
    queryKey: [
      'purchase-orders',
      shopId,
      filters.status ?? 'all',
      filters.vendor ?? '',
      filters.dateFrom ?? '',
      filters.dateTo ?? '',
      filters.search ?? '',
    ],
    enabled: !!shopId,
    queryFn: async (): Promise<PurchaseOrder[]> => {
      let q = (supabase as any)
        .from('purchase_orders')
        .select('*')
        .eq('shop_id', shopId)
        .order('created_at', { ascending: false });

      if (filters.status && filters.status !== 'all') q = q.eq('status', filters.status);
      if (filters.vendor && filters.vendor.trim()) q = q.ilike('vendor_name', `%${filters.vendor.trim()}%`);

      if (filters.dateFrom) {
        const from = new Date(`${filters.dateFrom}T00:00:00`).toISOString();
        q = q.gte('created_at', from);
      }
      if (filters.dateTo) {
        const to = new Date(`${filters.dateTo}T23:59:59.999`).toISOString();
        q = q.lte('created_at', to);
      }

      if (filters.search && filters.search.trim()) {
        const term = filters.search.trim().replace(/[,()]/g, '');
        q = q.or(`po_number.ilike.%${term}%,vendor_name.ilike.%${term}%`);
      }

      const { data, error } = await q;
      if (error) throw error;
      return (data ?? []) as PurchaseOrder[];
    },
  });
}
